import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxDataGridModule, DxButtonModule } from 'devextreme-angular';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions, PageOrientation, PageSize } from 'pdfmake/interfaces';
import {
  BreakdownContext,
  MonthlySummaryResponse,
  PromotionRow
} from '../effectiveness.models';

const pdf = pdfMake;
(pdf as any).vfs = pdfFonts.vfs;

@Component({
  selector: 'app-monthly-summary',
  templateUrl: './monthly-summary.component.html',
  styleUrls: ['./monthly-summary.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    DxDataGridModule,
    DxButtonModule
  ]
})
export class MonthlySummaryComponent {

  @Input() data: MonthlySummaryResponse | null = null;
  @Input() periodStart: string = '';
  @Input() periodEnd: string = '';
  @Input() selectedBuyingGroupName: string | null = null;
  @Input() selectedPromotionType: string | null = null;

  @Output() breakdownRequested = new EventEmitter<BreakdownContext>();

  onPromotionRowClick(e: any): void {
    const row: PromotionRow = e.data;
    this.breakdownRequested.emit({
      promotionId: row.promotion_id,
      promotionName: row.promotion_name,
      buyingGroup: row.buying_group,
      type: row.type,
      totalVolume: row.volume
    });
  }

  formatNumber(value: number | null): string {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString('en-AU');
  }

  formatCurrency(value: number | null): string {
    if (value === null || value === undefined) return 'N/A';
    return '$' + value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatPercent(value: number | null): string {
    if (value === null || value === undefined) return 'N/A';
    return value.toFixed(1) + '%';
  }

  formatChange(value: number | null, suffix: string = '%'): string {
    if (value === null || value === undefined) return '';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}${suffix}`;
  }

  formatPromotionChange(value: number | null): string {
    if (value === null || value === undefined) return '';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}`;
  }

  isPositive(value: number | null): boolean {
    return value !== null && value !== undefined && value > 0;
  }

  isNegative(value: number | null): boolean {
    return value !== null && value !== undefined && value < 0;
  }

  getTypeClass(type: string): string {
    const normalised = type?.toLowerCase().replace(/[\s-]/g, '') || '';
    if (normalised === '2week') return 'type-2week';
    if (normalised === 'monthly') return 'type-monthly';
    if (normalised === 'edlp') return 'type-edlp';
    if (normalised === 'trade') return 'type-trade';
    return 'type-default';
  }

  private formatPeriodDisplay(): string {
    if (!this.periodStart || !this.periodEnd) return 'Unknown';
    const format = (iso: string) => {
      const [year, m, d] = iso.split('-').map(v => parseInt(v, 10));
      return new Date(year, m - 1, d).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    };
    return `${format(this.periodStart)} to ${format(this.periodEnd)}`;
  }

  private getPeriodSlug(): string {
    if (!this.periodStart || !this.periodEnd) return 'unknown';
    return `${this.periodStart}_to_${this.periodEnd}`;
  }

  private getBuyingGroupLabel(): string {
    return this.selectedBuyingGroupName || 'All Buying Groups';
  }

  private getPromotionTypeLabel(): string {
    return this.selectedPromotionType || 'All Promotion Types';
  }

  protected saveFile(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }

  protected downloadPdf(docDefinition: TDocumentDefinitions, filename: string): void {
    pdf.createPdf(docDefinition).download(filename);
  }

  private getTimestamp(): string {
    const now = new Date();
    return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
  }

  exportExcel(): void {
    if (!this.data?.promotions?.length) return;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Monthly Performance');

    const periodDisplay = this.formatPeriodDisplay();
    const buyingGroupLabel = this.getBuyingGroupLabel();
    const promotionTypeLabel = this.getPromotionTypeLabel();

    // Header rows
    const titleRow = worksheet.addRow(['Monthly Performance Report']);
    titleRow.font = { bold: true, size: 14 };
    worksheet.addRow([`Period: ${periodDisplay}`]);
    worksheet.addRow([`Buying Group: ${buyingGroupLabel}`]);
    worksheet.addRow([`Promotion Type: ${promotionTypeLabel}`]);
    worksheet.addRow([]); // blank row

    // Column headers
    const headerLabels = ['Promotion', 'Buying Group', 'Type', 'Volume (cases)', 'Total ($)'];
    const headerRow = worksheet.addRow(headerLabels);

    // Style header row
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF182351' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    });

    // Data rows
    for (const row of this.data.promotions) {
      worksheet.addRow([
        row.promotion_name,
        row.buying_group,
        row.type,
        row.volume,
        row.total_value !== null ? row.total_value : '\u2014'
      ]);
    }

    // Column widths
    worksheet.getColumn(1).width = 30; // Promotion
    worksheet.getColumn(2).width = 20; // Buying Group
    worksheet.getColumn(3).width = 12; // Type
    worksheet.getColumn(4).width = 15; // Volume
    worksheet.getColumn(5).width = 15; // Total ($)

    // Number formatting for numeric columns
    const dataStartRow = 7; // row 6 is header, data starts at 7
    for (let i = dataStartRow; i <= dataStartRow + this.data.promotions.length - 1; i++) {
      const volumeCell = worksheet.getCell(i, 4);
      const valueCell = worksheet.getCell(i, 5);

      if (typeof volumeCell.value === 'number') {
        volumeCell.numFmt = '#,##0';
      }
      if (typeof valueCell.value === 'number') {
        valueCell.numFmt = '$#,##0.00';
      }
    }

    const filename = `Monthly_Performance_${this.getPeriodSlug()}_${this.getTimestamp()}.xlsx`;

    workbook.xlsx.writeBuffer().then((buffer) => {
      this.saveFile(new Blob([buffer], { type: 'application/octet-stream' }), filename);
    }).catch((error) => {
      console.error('Excel export failed:', error);
    });
  }

  exportPdf(): void {
    if (!this.data?.promotions?.length) return;

    const periodDisplay = this.formatPeriodDisplay();
    const buyingGroupLabel = this.getBuyingGroupLabel();
    const promotionTypeLabel = this.getPromotionTypeLabel();
    const generatedDate = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

    // Table header row
    const tableHeaders = ['Promotion', 'Buying Group', 'Type', 'Volume', 'Total ($)']
      .map(text => ({ text: text.toUpperCase(), style: 'tableHeader' }));

    // Table data rows
    const tableBody = this.data.promotions.map((row: PromotionRow, index: number) => {
      const fillColor = index % 2 === 0 ? '#F9FAFB' : '#FFFFFF';
      return [
        { text: row.promotion_name, fillColor },
        { text: row.buying_group, fillColor },
        { text: row.type, fillColor },
        { text: row.volume !== null ? row.volume.toLocaleString('en-AU') : '\u2014', alignment: 'right' as const, fillColor },
        { text: row.total_value !== null ? this.formatCurrency(row.total_value) : '\u2014', alignment: 'right' as const, fillColor }
      ];
    });

    const documentDefinition: TDocumentDefinitions = {
      pageSize: 'A4' as PageSize,
      pageOrientation: 'landscape' as PageOrientation,
      pageMargins: [40, 40, 40, 60],
      content: [
        { text: 'Monthly Performance Report', style: 'reportTitle' },
        {
          columns: [
            { text: `Period: ${periodDisplay}`, style: 'filterLabel' },
            { text: `Buying Group: ${buyingGroupLabel}`, style: 'filterLabel' },
            { text: `Promotion Type: ${promotionTypeLabel}`, style: 'filterLabel' }
          ],
          margin: [0, 4, 0, 4] as [number, number, number, number]
        },
        { text: `Generated: ${generatedDate}`, style: 'generatedDate', margin: [0, 0, 0, 12] as [number, number, number, number] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 60, 70],
            body: [tableHeaders, ...tableBody]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#E0E0E0'
          }
        }
      ],
      styles: {
        reportTitle: {
          fontSize: 16,
          bold: true,
          color: '#0E1218',
          margin: [0, 0, 0, 8] as [number, number, number, number]
        },
        filterLabel: {
          fontSize: 9,
          color: '#5A6A7D'
        },
        generatedDate: {
          fontSize: 8,
          color: '#5A6A7D',
          italics: true
        },
        tableHeader: {
          bold: true,
          fontSize: 8,
          color: '#FFFFFF',
          fillColor: '#182351',
          margin: [2, 4, 2, 4] as [number, number, number, number]
        }
      }
    };

    const filename = `Monthly_Performance_${this.getPeriodSlug()}_${this.getTimestamp()}.pdf`;

    this.downloadPdf(documentDefinition, filename);
  }
}
